/**
 * Buttons shown to the content team that link to editing the current content.
 */
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {Text} from "@khanacademy/wonder-blocks-core";
import Link from "@khanacademy/wonder-blocks-link";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {mediaQueries} from "@repo/shared-styles";

// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedIcon from "#shared-styles-package/deprecated-icon.tsx";

// sync-start:native-content-locales 1172605420 https://github.com/Khan/webapp/blob/master/services/content-editing/locales/native_content.go
// We're incrementally introducing, in the Course Editor, the ability to
// natively create and edit learnable content items that do not need to be
// translated to be published, starting with the locales below.
const nativeContentLocales: ReadonlyArray<string> = ["en", "es", "pt", "lol"];
// sync-end:native-content-locales

// SVG icons used by icon.jsx
const editContentIcon = {
    path: "M41.209 53.753l5.39 0l0 5.39l3.136 0l6.468-6.517-8.477-8.526-6.517 6.517l0 3.136zm33.173-34.937q-.882-.882-1.862.049l-19.6 19.6q-.931.98-.049 1.862t1.862-.049l19.6-19.6q.931-.98.049-1.862zm-38.563 45.668l0-16.121l37.632-37.632 16.17 16.121-37.632 37.632l-16.17 0zm43.022-12.397l0 10.633q-.049 6.713-4.753 11.417t-11.368 4.704l-46.599 0q-6.713 0-11.417-4.753t-4.704-11.368l0-46.599q0-6.664 4.753-11.417t11.368-4.704l46.599 0q3.528 0 6.566 1.372.833.392.98 1.323t-.49 1.617l-2.744 2.744q-.784.784-1.96.441t-2.352-.343l-46.599 0q-3.675 0-6.321 2.646t-2.646 6.321l0 46.599q0 3.675 2.646 6.321t6.321 2.646l46.599 0q3.675 0 6.321-2.646t2.646-6.321l0-7.056q0-.735.49-1.225l3.577-3.577q.833-.833 1.96-.392t1.127 1.617zm7.203-51.646q2.254 0 3.773 1.568l8.526 8.526q1.568 1.568 1.568 3.822t-1.568 3.773l-5.145 5.145-16.121-16.121 5.145-5.145q1.568-1.568 3.822-1.568z",
    width: 100,
    height: 78.912,
} as const;

type EditorShortcutsProps = {
    editContentUrl: string | null | undefined;
    kaLocale: string;
};

const EditorShortcuts = (props: EditorShortcutsProps): React.ReactElement => (
    <Text style={styles.editorShortcuts}>
        {props.editContentUrl &&
            // Show editor link for locales that allow native content creation.
            // TODO(michaelpolyak): We should be checking `source_language`
            // of content if `kaLocale` is not "en", to prevent opening editor
            // for imported content.
            nativeContentLocales.includes(props.kaLocale) && (
                <Link
                    href={props.editContentUrl}
                    style={[styles.editContentShortcut, styles.linkColor]}
                    target="_blank"
                    title="Edit this content"
                >
                    {/* Size of 25 is required to ensure touch target renders */}
                    {/* at minimum WCAG size of 24x24px. */}
                    <DeprecatedIcon icon={editContentIcon} size={25} />
                </Link>
            )}
    </Text>
);

const styles = StyleSheet.create({
    editContentShortcut: {
        marginRight: 5,
    },
    editorShortcuts: {
        fontSize: "70%",

        [mediaQueries.mdOrSmaller]: {
            marginLeft: 10,
        },
        [mediaQueries.lg]: {
            marginLeft: 15,
        },
        [mediaQueries.xl]: {
            marginLeft: 20,
        },
    },
    linkColor: {
        // TODO(colin): this overrides the wonder blocks link color to match the
        // former color. Should we update to the wonder blocks default?
        color: color.offBlack,
    },
});

export default EditorShortcuts;
